<script setup>
// Lead Scoring — badge visual do score + perfil. Aparece no sidebar da
// conversa, lista de conversas e detalhe do Kanban item. Zero render
// quando contato nao tem score.
//
// Visual: icone Lucide por perfil (sem emoji) + numero 0-100 + label
// opcional. Cores/limiares vêm da régua única em useLeadTier
// (perfil da IA tem prioridade; senão >= 80 quente / >= 50 morno / frio).
//
// Interacao: quando `contactId` passado, badge vira clicavel — click
// abre popover com breakdown dos criterios do ultimo scoring. Util pra
// debugar "por que esse lead ficou com 72?".
//
// Props:
//   score: number | null
//   profile: 'HOT' | 'WARM' | 'COLD' (case insensitive)
//   size: 'xs' | 'sm' | 'md'
//   showLabel: bool | null (null = auto por size)
//   contactId: number | null (habilita o popover de debug)
import { computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import ContactAPI from 'dashboard/api/contacts';
import { useLeadTier, resolveLeadTier } from 'dashboard/composables/useLeadTier';

const props = defineProps({
  score:     { type: [Number, String], default: null },
  profile:   { type: String,           default: null },
  size:      { type: String,           default: 'sm' },
  showLabel: { type: Boolean,          default: null },
  contactId: { type: [Number, String], default: null },
});

const numericScore = computed(() => {
  if (props.score === null || props.score === undefined || props.score === '') return null;
  const n = Number(props.score);
  return Number.isFinite(n) ? n : null;
});

const hasAnyData = computed(() => numericScore.value !== null || !!props.profile);

// Régua única de tier — perfil da IA tem prioridade sobre o score
const { colorClass: tierColorClass } = useLeadTier(
  () => numericScore.value,
  () => props.profile
);

// Icone + label só quando ha perfil explicito (mantém o badge "só numero"
// quando o contato tem score mas a IA ainda não classificou o perfil)
const profileMeta = computed(() => {
  if (!props.profile) return null;
  const meta = resolveLeadTier(null, props.profile);
  if (!meta) return null;
  return { icon: meta.icon, label: meta.label };
});

const tierClass = computed(
  () => tierColorClass.value || resolveLeadTier(0).colorClass
);

const sizeClass = computed(() => {
  switch (props.size) {
    case 'xs': return 'text-[9px] px-1 py-0.5 gap-0.5';
    case 'md': return 'text-xs px-2 py-1 gap-1';
    default:   return 'text-[10px] px-1.5 py-0.5 gap-1';
  }
});

const iconSizeClass = computed(() => (props.size === 'md' ? 'size-3.5' : 'size-3'));

const shouldShowLabel = computed(() => {
  if (props.showLabel !== null) return props.showLabel;
  return props.size === 'md';
});

const tooltipText = computed(() => {
  const parts = [];
  if (numericScore.value !== null) parts.push(`Score do lead: ${numericScore.value}/100`);
  if (profileMeta.value) parts.push(`Perfil: ${profileMeta.value.label}`);
  if (props.contactId) parts.push('Clique para ver os criterios');
  return parts.join(' - ');
});

const clickable = computed(() => !!props.contactId);

// Popover state — abre ao clicar, fecha ao clicar fora ou ESC.
const popoverOpen = ref(false);
const latestScore = ref(null);
const loading = ref(false);
const loadError = ref(null);
const containerRef = ref(null);
const popoverRef = ref(null);
// Position do popover (fixed, em pixels). Atualizado sempre que abre,
// scroll ou resize — pra acompanhar o badge quando a pagina mexe.
const popoverPos = ref({ top: 0, left: 0 });
const POPOVER_WIDTH = 320;  // combinar com w-80 no template
const POPOVER_GAP = 8;      // gap entre badge e popover

// Calcula posicao fixed do popover relativa ao viewport. Alinha a
// borda direita do popover com a borda direita do badge (anti-overflow
// na sidebar direita). Flipa pra cima se nao tem espaco abaixo.
function computePopoverPosition() {
  const badge = containerRef.value?.querySelector('button, span[class*="rounded-full"]');
  const ref = badge || containerRef.value;
  if (!ref) return;
  const rect = ref.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = rect.right - POPOVER_WIDTH;
  if (left < 8) left = 8; // evita colar na borda esquerda
  if (left + POPOVER_WIDTH > vw - 8) left = vw - POPOVER_WIDTH - 8;

  let top = rect.bottom + POPOVER_GAP;
  const approxHeight = 420; // altura tipica; se estoura cai pra cima
  if (top + approxHeight > vh - 8 && rect.top > approxHeight + POPOVER_GAP) {
    top = rect.top - POPOVER_GAP - approxHeight;
  }

  popoverPos.value = { top, left };
}

function handleScrollOrResize() {
  if (popoverOpen.value) computePopoverPosition();
}

async function togglePopover() {
  if (!clickable.value) return;
  if (popoverOpen.value) {
    popoverOpen.value = false;
    return;
  }
  popoverOpen.value = true;
  await nextTick();
  computePopoverPosition();
  if (latestScore.value) return; // cache no mesmo mount
  loading.value = true;
  loadError.value = null;
  try {
    const { data } = await ContactAPI.getLeadScores(props.contactId, { limit: 1 });
    const scores = data?.data || data || [];
    latestScore.value = Array.isArray(scores) ? scores[0] : null;
    await nextTick();
    computePopoverPosition();
  } catch (e) {
    loadError.value = e?.response?.data?.error || 'Falha ao carregar detalhes';
  } finally {
    loading.value = false;
  }
}

function handleOutsideClick(event) {
  if (!popoverOpen.value) return;
  const insideBadge = containerRef.value && containerRef.value.contains(event.target);
  const insidePopover = popoverRef.value && popoverRef.value.contains(event.target);
  if (!insideBadge && !insidePopover) popoverOpen.value = false;
}

function handleEsc(event) {
  if (event.key === 'Escape' && popoverOpen.value) popoverOpen.value = false;
}

onMounted(() => {
  document.addEventListener('mousedown', handleOutsideClick);
  document.addEventListener('keydown', handleEsc);
  window.addEventListener('scroll', handleScrollOrResize, true);
  window.addEventListener('resize', handleScrollOrResize);
});
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleOutsideClick);
  document.removeEventListener('keydown', handleEsc);
  window.removeEventListener('scroll', handleScrollOrResize, true);
  window.removeEventListener('resize', handleScrollOrResize);
});

watch(popoverOpen, async open => {
  if (open) {
    await nextTick();
    computePopoverPosition();
  }
});

// Normaliza o hash `analysis` (jsonb) em lista ordenada decrescente.
// Shape esperado: { criterio_x: { score, evidence, notes }, ... }
const criteriaList = computed(() => {
  const analysis = latestScore.value?.analysis;
  if (!analysis || typeof analysis !== 'object') return [];
  return Object.entries(analysis)
    .map(([name, data]) => ({
      name,
      score:    typeof data === 'object' ? Number(data?.score) : Number(data),
      evidence: typeof data === 'object' ? (data?.evidence || '') : '',
      notes:    typeof data === 'object' ? (data?.notes || '') : '',
    }))
    .filter(c => Number.isFinite(c.score))
    .sort((a, b) => b.score - a.score);
});

const scoredAtFormatted = computed(() => {
  const iso = latestScore.value?.scored_at;
  if (!iso) return null;
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
});

function criterionBarClass(score) {
  return (resolveLeadTier(score) || resolveLeadTier(0)).barClass;
}
</script>

<template>
  <span ref="containerRef" class="relative inline-block">
    <component
      :is="clickable ? 'button' : 'span'"
      v-if="hasAnyData"
      :type="clickable ? 'button' : undefined"
      class="inline-flex items-center rounded-full font-medium border whitespace-nowrap"
      :class="[tierClass, sizeClass, clickable ? 'cursor-pointer hover:opacity-90 transition-opacity' : '']"
      :title="tooltipText"
      @click.stop="togglePopover"
    >
      <span v-if="profileMeta" :class="[profileMeta.icon, iconSizeClass, 'shrink-0']" />
      <span v-if="numericScore !== null">{{ numericScore }}</span>
      <span v-if="shouldShowLabel && profileMeta">{{ profileMeta.label }}</span>
    </component>

    <!-- Popover renderizado via Teleport pro body — evita clipping por
         containers com overflow-hidden e problemas de z-index em
         stacking contexts aninhados. Posicao calculada via
         getBoundingClientRect (position: fixed) pra alinhar com o badge. -->
    <Teleport to="body">
    <div
      v-if="popoverOpen"
      ref="popoverRef"
      class="fixed z-[9999] w-80 max-w-[calc(100vw-16px)] rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl p-4 text-left"
      :style="{ top: popoverPos.top + 'px', left: popoverPos.left + 'px' }"
    >
      <header class="flex items-center justify-between mb-3">
        <h4 class="text-sm font-semibold text-n-slate-12">Como esse score foi calculado</h4>
        <button
          type="button"
          class="text-n-slate-11 hover:text-n-slate-12"
          @click.stop="popoverOpen = false"
        >
          <span class="i-ri-close-line size-4" />
        </button>
      </header>

      <div v-if="loading" class="text-xs text-n-slate-11">Carregando analise...</div>
      <div v-else-if="loadError" class="text-xs text-n-ruby-11">{{ loadError }}</div>
      <div v-else-if="!latestScore" class="text-xs text-n-slate-11">Sem analise disponivel ainda.</div>

      <div v-else class="space-y-3">
        <div class="flex items-center justify-between text-xs text-n-slate-11">
          <span v-if="scoredAtFormatted">Analisado em {{ scoredAtFormatted }}</span>
          <span v-if="latestScore.model" class="font-mono">{{ latestScore.model }}</span>
        </div>

        <div class="flex items-baseline gap-2">
          <span class="text-2xl font-extrabold text-n-slate-12">{{ latestScore.score ?? '-' }}</span>
          <span class="text-xs text-n-slate-11">/ 100</span>
          <span v-if="profileMeta" class="ml-auto inline-flex items-center gap-1 text-xs text-n-slate-12">
            <span :class="[profileMeta.icon, 'size-3.5']" />
            {{ profileMeta.label }}
          </span>
        </div>

        <div v-if="criteriaList.length">
          <p class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-2">Criterios</p>
          <ul class="space-y-2">
            <li v-for="c in criteriaList" :key="c.name">
              <div class="flex items-baseline justify-between gap-2">
                <span class="text-xs font-medium text-n-slate-12 capitalize">{{ c.name }}</span>
                <span class="text-xs font-mono text-n-slate-12">{{ c.score }}</span>
              </div>
              <div class="h-1.5 bg-n-alpha-2 rounded-full overflow-hidden mt-1">
                <div
                  class="h-full rounded-full transition-all"
                  :class="criterionBarClass(c.score)"
                  :style="{ width: `${Math.max(0, Math.min(100, c.score))}%` }"
                />
              </div>
              <p v-if="c.evidence" class="text-[11px] text-n-slate-11 mt-1 italic">{{ c.evidence }}</p>
              <p v-if="c.notes" class="text-[11px] text-n-slate-11 mt-0.5">{{ c.notes }}</p>
            </li>
          </ul>
        </div>

        <div v-if="latestScore.suggestions" class="pt-2 border-t border-n-weak">
          <p class="text-[11px] uppercase tracking-wide text-n-slate-11 mb-1">Sugestoes do modelo</p>
          <p class="text-xs text-n-slate-12 leading-relaxed">{{ latestScore.suggestions }}</p>
        </div>
      </div>
    </div>
    </Teleport>
  </span>
</template>
